import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchEntity, sourceDocumentHref } from '../api/client.js';
import { humanizeClusterKey } from '../lib/clusterMeta.js';
import { reviewableSources } from '../lib/evidenceReadiness.js';
import { practiceItemAttachment } from '../lib/workItems.js';
import type { WorkItemSummary } from '../types/workflow.js';
import { NewLessonQuestionLink } from './NewLessonQuestionLink.js';

/** Matches the queue drawer's evidence panel so the two review surfaces agree. */
const MAX_VISIBLE_EXCERPTS = 3;

export function PracticeItemMetadata({ item }: { item: WorkItemSummary }) {
  return (
    <div className="grid gap-5 md:grid-cols-2">
      <section>
        <MetadataHeading>About this item</MetadataHeading>
        <p className="text-sm leading-relaxed text-slate-700">{item.rationale}</p>
      </section>
      <section className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
        <MetadataHeading>Review focus</MetadataHeading>
        <p className="text-sm font-medium leading-relaxed text-indigo-950">
          {item.review_summary || 'Review the item for accuracy, clarity, and alignment.'}
        </p>
      </section>
      <ConnectedPracticeLessons item={item} />
      <PracticeItemTopics item={item} />
      <PracticeItemReferences item={item} />
    </div>
  );
}

export function ConnectedPracticeLessons({ item }: { item: WorkItemSummary }) {
  const attachment = practiceItemAttachment(item);
  const entityId = attachment === 'attached' ? item.attachedEntityId : undefined;
  const [title, setTitle] = useState(entityId == null ? '' : `#${entityId}`);

  useEffect(() => {
    if (entityId == null) {
      setTitle('');
      return;
    }
    let cancelled = false;
    setTitle(`#${entityId}`);
    void fetchEntity(item.courseKey, entityId)
      .then((entity) => {
        if (!cancelled) setTitle(entity.title?.trim() || `#${entityId}`);
      })
      .catch(() => {
        if (!cancelled) setTitle(`#${entityId}`);
      });
    return () => {
      cancelled = true;
    };
  }, [entityId, item.courseKey]);

  return (
    <section aria-label="Connected micro-lessons">
      <MetadataHeading>Connected micro-lessons</MetadataHeading>
      <ul className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <li className="flex items-start gap-2">
          <span className="material-symbols-outlined mt-0.5 text-[18px] text-indigo-500">hub</span>
          {/*
            The lesson link below ends in `/transcript`, NEVER the bare
            `/courses/:courseId/entities/:entityId` editor route. That route renders a Save button on
            a path carrying `CourseGuard` + `CsrfGuard` and no staff-role guard, and it bypasses the
            release pipeline entirely — no work item, no snapshot, no inverse patch, so no rollback
            (`L-1246`). It pointed at the editor until 2026-08-28, and it became materially more
            dangerous the moment a reviewer held a grant on more than one exam level: the destination
            could then be a Level II or Level III lesson opened from a Level I practice item.
          */}
          <div className="min-w-0">
            {item.op === 'create' && item.intendedParent?.newLessonWorkItemId ? <NewLessonQuestionLink item={item} /> : entityId != null ? (
              <Link
                className="inline-flex rounded-full border border-indigo-200 bg-white px-3 py-1 text-sm font-bold text-indigo-700 hover:bg-indigo-50"
                to={`/courses/${encodeURIComponent(item.courseKey)}/entities/${entityId}/transcript`}
              >
                {title}
              </Link>
            ) : (
              <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm font-bold text-amber-800">
                {item.intendedParent?.lessonSlug ?? 'Intended lesson unavailable'}
              </span>
            )}
            {entityId == null && item.intendedParent && !(item.op === 'create' && item.intendedParent.newLessonWorkItemId) ? (
              <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                Attaches automatically once the lesson lands.
              </p>
            ) : null}
          </div>
        </li>
      </ul>
    </section>
  );
}

export function PracticeItemTopics({ item }: { item: WorkItemSummary }) {
  const topics = useMemo(() => {
    const nodePath = item.intendedParent?.nodePath;
    if (nodePath?.trim()) return nodePath.split('>').map((part) => part.trim()).filter(Boolean);
    return humanizeClusterKey(item.clusterKey).split(' · ').map((part) => part.trim()).filter(Boolean);
  }, [item.clusterKey, item.intendedParent?.nodePath]);

  return (
    <section>
      <MetadataHeading>Topics</MetadataHeading>
      <div className="flex flex-wrap gap-1.5">
        {topics.map((topic) => (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700" key={topic}>
            {topic}
          </span>
        ))}
      </div>
    </section>
  );
}

export function PracticeItemReferences({ item }: { item: WorkItemSummary }) {
  // `reviewableSources` is the SINGLE definition of what counts as openable evidence, shared with
  // the missing-evidence gate — a panel that says "No PDF references attached" while the gate stays
  // quiet (or the reverse) would be worse than either alone.
  const references = useMemo(() => reviewableSources(item), [item]);

  return (
    <section>
      <MetadataHeading>Reference material</MetadataHeading>
      {references.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {references.map((ref) => {
            const page = ref.printedPage != null
              ? `Printed page ${ref.printedPage}`
              : ref.filePage != null ? `Page ${ref.filePage}` : '';
            const hidden = Math.max(0, ref.notes.length - MAX_VISIBLE_EXCERPTS);
            return (
              <li className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs" key={`${ref.path}-${ref.filePage ?? ''}-${ref.printedPage ?? ''}`}>
                <div className="flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[16px] text-rose-500">picture_as_pdf</span>
                  {ref.availability === 'missing' ? (
                    <>
                      <span className="font-semibold text-slate-700">{ref.fileName}{page ? ` · ${page}` : ''}</span>
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">Source not ingested</span>
                    </>
                  ) : (
                    <a
                      className="font-semibold text-indigo-700 hover:underline"
                      href={sourceDocumentHref(ref.path, ref.filePage)}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {ref.fileName}{page ? ` · ${page}` : ''}
                    </a>
                  )}
                </div>
                {/* The verbatim curriculum sentence the item depends on — without it the reviewer
                    can only see WHICH page was cited, not whether it supports the claim. */}
                {ref.notes.length > 0 ? (
                  <div className="mt-1.5 flex flex-col gap-1.5">
                    {ref.notes.slice(0, MAX_VISIBLE_EXCERPTS).map((note) => (
                      <blockquote
                        className="border-l-2 border-amber-300 bg-amber-50 px-2.5 py-1.5 leading-relaxed text-slate-700 italic"
                        key={note}
                      >
                        {note}
                      </blockquote>
                    ))}
                    {hidden > 0 ? (
                      <p className="text-slate-400">+{hidden} more excerpt{hidden === 1 ? '' : 's'}</p>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-xs text-slate-400">No PDF references attached.</p>
      )}
    </section>
  );
}

function MetadataHeading({ children }: { children: string }) {
  return <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{children}</h4>;
}
